import React, { useState, useEffect, useRef } from 'react';
import { HiOutlineLocationMarker, HiOutlineCalendar } from 'react-icons/hi';
import './APropos.css';
import profile from '../images/profile.png';
import { supabase } from '../supabaseClient';
import { resolveStorageUrl } from '../utils/storageUrl';
// import LogoCarousel from './LogoCarousel';
import FlagCarousel from './FlagCarousel';
import PDFButtons from './PDFButtons';
import ScientificCommittee from './ScientificCommittee';
import { useLanguage } from '../hooks/useLanguage';
import { getTranslation } from '../utils/i18n';

function APropos({ edition: customEdition }) {
  const [aproposData, setAproposData] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentLang = useLanguage();
  const [error, setError] = useState(null);
  // Add state to track which orgs are expanded
  const [expandedOrgs, setExpandedOrgs] = useState({});
  const [expanded, setExpanded] = useState({});
  const infoRefs = useRef([]);
  const [maxInfoHeight, setMaxInfoHeight] = useState(0);

  useEffect(() => {
    loadAproposData();
  }, [customEdition]);

  const loadAproposData = async () => {
    try {
      setLoading(true);
      
      let currentEditionYear = customEdition || '2024';
      let currentEditionId = null;

      if (!customEdition) {
        // Get active edition first
        const { data: activeEdition } = await supabase
          .from('editions')
          .select('year, id')
          .eq('is_active', true)
          .single();
        
        if (activeEdition) {
          currentEditionYear = activeEdition.year;
          currentEditionId = activeEdition.id;
        }
      }

      if (!currentEditionId) {
        // Get edition by year
        const { data: editionByYear } = await supabase
          .from('editions')
          .select('id')
          .eq('year', currentEditionYear)
          .single();
        
        if (editionByYear) {
          currentEditionId = editionByYear.id;
        }
      }

      if (!currentEditionId) {
        throw new Error(`Edition ${currentEditionYear} not found`);
      }

      // Get main apropos data
      const { data: mainData } = await supabase
        .from('apropos_data')
        .select('*')
        .eq('edition_id', currentEditionId)
        .single();

      // Get event details
      const { data: eventDetails } = await supabase
        .from('apropos_event_details')
        .select('*')
        .eq('edition_id', currentEditionId)
        .order('display_order');

      // Get committee members
      const { data: committeeMembers } = await supabase
        .from('apropos_committee_members')
        .select('*')
        .eq('edition_id', currentEditionId)
        .order('display_order');

      const data = {
        pageHeader: {
          title: mainData?.page_header_title || 'À Propos'
        },
        eventIntro: {
          paragraph1: mainData?.event_intro_paragraph1 || '',
          paragraph2: mainData?.event_intro_paragraph2 || ''
        },
        images: {
          image1_url: resolveStorageUrl(mainData?.image1_url || ''),
          image1_title: mainData?.image1_title || '',
          image2_url: resolveStorageUrl(mainData?.image2_url || ''),
          image2_title: mainData?.image2_title || '',
          image3_url: resolveStorageUrl(mainData?.image3_url || ''),
          image3_title: mainData?.image3_title || ''
        },
        committeeSection: {
          title: mainData?.committee_section_title || 'Comité d\'organisation du congrès',
          honoraryCommitteeTop: mainData?.honorary_committee_top || '',
          honoraryCommitteeBottom: mainData?.honorary_committee_bottom || ''
        },
        eventDetails: (eventDetails || []).map(detail => ({
          id: detail.id,
          title: detail.title,
          content: detail.content,
          icon: detail.icon
        })),
        committeeMembers: (committeeMembers || []).map(member => ({
          id: member.id,
          name: member.name,
          title: member.title,
          organization: member.organization,
          image: resolveStorageUrl(member.image_url, 'speaker-images', member.image_path) || profile,
          logo: resolveStorageUrl(member.company_logo_url, 'speaker-images', member.company_logo_path),
          // Normalize booleans: DB may return 1/0 or "1"/"0"
          is_honorary:   member.is_honorary   === 1 || member.is_honorary   === '1' || member.is_honorary   === true,
          is_organizing: member.is_organizing === 1 || member.is_organizing === '1' || member.is_organizing === true,
          is_president:  member.is_president  === 1 || member.is_president  === '1' || member.is_president  === true,
          is_scientific: member.is_scientific === 1 || member.is_scientific === '1' || member.is_scientific === true,
          // Per-category order columns for correct within-section sorting
          order_president:  member.order_president  || 0,
          order_scientific: member.order_scientific || 0,
          order_honorary:   member.order_honorary   || 0,
          order_organizing: member.order_organizing || 0,
          display_order:    member.display_order    || 0
        }))
      };

      setAproposData(data);
      setError(null);
    } catch (error) {
      console.error('Error loading apropos data:', error);
      setError('Failed to load data');
      // Set empty data structure as fallback
      setAproposData({
        pageHeader: { title: "À Propos" },
        eventIntro: { paragraph1: "", paragraph2: "" },
        images: { image1_url: "", image2_url: "", image3_url: "" },
        committeeSection: { title: "Comité" },
        eventDetails: [],
        committeeMembers: []
      });
    } finally {
      setLoading(false);
    }
  };

  const getIconComponent = (iconName) => {
    switch (iconName) {
      case 'HiOutlineLocationMarker':
        return <HiOutlineLocationMarker />;
      case 'HiOutlineCalendar':
        return <HiOutlineCalendar />;
      default:
        return <HiOutlineLocationMarker />;
    }
  };

  // Helper to truncate and toggle organization name
  const renderOrganization = (org, memberId) => {
    const maxLen = 32; // adjust as needed
    if (!org) return null;
    const isLong = org.length > maxLen;
    const isExpanded = expandedOrgs[memberId];
    if (!isLong) return <p>{org}</p>;
    return (
      <p className={isExpanded ? "expanded" : ""}>
        {isExpanded ? org : org.slice(0, maxLen) + '...'}
        {!isExpanded && (
          <span
            style={{ color: '#000000ff', cursor: 'pointer', marginLeft: 4, fontWeight: 500 }}
            onClick={() => setExpandedOrgs(prev => ({ ...prev, [memberId]: true }))}
          >
            +voir plus
          </span>
        )}
      </p>
    );
  };

  // Update max height for info boxes on committee change
  useEffect(() => {
    if (!infoRefs.current) return;
    const heights = infoRefs.current.map(ref => ref ? ref.offsetHeight : 0);
    setMaxInfoHeight(Math.max(...heights, 0));
  }, [aproposData?.committeeMembers]);

  // Toggle expand/collapse for a committee member
  const toggleExpand = (id) => {
    setExpanded(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Helper to truncate text and add "Voir plus/moins"
  const renderExpandableText = (text, id, maxLength = 40) => {
    if (!text) return null;
    if (text.length <= maxLength) return <span>{text}</span>;
    const isOpen = expanded[id];
    return (
      <>
        <span>
          {isOpen ? text : text.slice(0, maxLength) + '...'}
        </span>
        <button
          className="voir-plus-btn"
          onClick={() => toggleExpand(id)}
          type="button"
        >
          {isOpen ? 'Voir moins' : 'Voir plus'}
        </button>
      </>
    );
  };

  if (loading) {
    return (
      <div className="apropos-page">
        <div className="apropos-container">
             {/* Replace text with animated loading spinner */}
              <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="apropos-page">
        <div className="apropos-container">
          <div className="apropos-header">
            <h1>À Propos</h1>
          </div>
          <div className="apropos-content">
            <p>Les données ne sont pas encore disponibles pour cette édition.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!aproposData) {
    return <div className="apropos-page">No data available</div>;
  }

  return (
    <div className="apropos-page">
      {/* <LogoCarousel /> */}
      <FlagCarousel />
      <PDFButtons />
      <div className="apropos-container">
        <div className="apropos-header">
          <h1>{getTranslation(aproposData.pageHeader?.title, currentLang)}</h1>
        </div>
        
        <div className="apropos-content">
          <div className="event-overview">
            {(aproposData.images?.image1_url || aproposData.images?.image2_url || aproposData.images?.image3_url) && (
              <div className="event-images">
                {aproposData.images?.image1_url && (
                  <div className="event-image-wrapper">
                    {aproposData.images.image1_title && <h3 className="event-image-title">{aproposData.images.image1_title}</h3>}
                    <div className="event-img-container">
                      <img src={aproposData.images.image1_url} alt="A Propos Image 1" className="event-img" />
                    </div>
                  </div>
                )}
                {aproposData.images?.image2_url && (
                  <div className="event-image-wrapper">
                    {aproposData.images.image2_title && <h3 className="event-image-title">{aproposData.images.image2_title}</h3>}
                    <div className="event-img-container">
                      <img src={aproposData.images.image2_url} alt="A Propos Image 2" className="event-img" />
                    </div>
                  </div>
                )}
                {aproposData.images?.image3_url && (
                  <div className="event-image-wrapper">
                    {aproposData.images.image3_title && <h3 className="event-image-title">{aproposData.images.image3_title}</h3>}
                    <div className="event-img-container">
                      <img src={aproposData.images.image3_url} alt="A Propos Image 3" className="event-img" />
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <div className="event-intro">
              <p className="intro-text">
                {getTranslation(aproposData.eventIntro?.paragraph1, currentLang)}
              </p>
              <p className="intro-text">
                {getTranslation(aproposData.eventIntro?.paragraph2, currentLang)}
              </p>
            </div>

            <div className="event-details">
              <div className="details-grid">
                {(aproposData.eventDetails || []).map(detail => (
                  <div key={detail.id} className="detail-item">
                    <div className="detail-icon1">
                      {getIconComponent(detail.icon)}
                    </div>
                    <div className="detail-content">
                      <h3>{detail.title}</h3>
                      {detail.content.split('\n').map((line, index) => (
                        <p key={index}>{line}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="congress-committee">
              
{/* 3. COMITE D'HONNEUR DU CONGRES */}
              {((aproposData.committeeMembers || []).filter(m => m.is_honorary).length > 0) && (
                <div className="honneur-list" style={{ marginBottom: '6vw' }}>
                  <h2>COMITE D'HONNEUR DU CONGRES</h2>
                  <div className="committee-grid uniform-grid">
                    {(aproposData.committeeMembers || [])
                      .filter(m => m.is_honorary)
                      .sort((a, b) => (a.order_honorary || 0) - (b.order_honorary || 0))
                      .map((member, idx) => (
                        <div key={member.id} className="committee-member" data-member-id={member.id}>
                          <div className="member-photo">
                            {member.logo && (
                              <div className="company-logo" >
                                <img
                                  src={member.logo}
                                  alt="Logo"
                                  style={{ width: '100%', height: '100%', objectFit: 'contain', maxWidth: '100%', maxHeight: '100%' }}
                                  onError={e => e.target.style.display = "none"}
                                />
                              </div>
                            )}
                            <img src={member.image || profile} alt={member.name} />
                          </div>
                          <div className="member-info">
                            <h4 style={{ margin: 0, textAlign: 'center' }}>{member.name}</h4>
                            <p style={{ color: '#f1ae1bff' }}>{renderExpandableText(member.title, member.id + '-title')}</p>
                            <p >{renderExpandableText(member.organization, member.id + '-org')}</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              
           
              


              {/* 1. PRÉSIDENT DU CONGRÈS */}
              {((aproposData.committeeMembers || []).filter(m => m.is_president).length > 0) && (
                <div className="common-committee-section president-list" style={{ marginBottom: '6vw' }}>
                  <h2>PRÉSIDENT DU CONGRÈS</h2>
                  <div className="committee-grid">
                    {(aproposData.committeeMembers || [])
                      .filter(m => m.is_president)
                      .sort((a, b) => (a.order_president || 0) - (b.order_president || 0))
                      .map((member, idx) => (
                        <div key={member.id} className="committee-member" data-member-id={member.id}>
                          <div className="member-photo">
                            {member.logo && (
                              <div className="company-logo" >
                                <img
                                  src={member.logo}
                                  alt="Logo"
                                  style={{ width: '100%', height: '100%', objectFit: 'contain', maxWidth: '100%', maxHeight: '100%' }}
                                  onError={e => e.target.style.display = "none"}
                                />
                              </div>
                            )}
                            <img src={member.image || profile} alt={member.name} />
                          </div>
                          <div className="member-info">
                            <h4 style={{ margin: 0, textAlign: 'center' }}>{member.name}</h4>
                            <p>{renderExpandableText(member.title, member.id + '-title')}</p>
                            <p>{renderExpandableText(member.organization, member.id + '-org')}</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* 3. COMITE D'ORGANISATION DU CONGRES */}
              {((aproposData.committeeMembers || []).filter(m => m.is_organizing).length > 0) && (
                <div className="common-committee-section organizing-committee-list">
                  <h2>{aproposData.committeeSection?.title || "Comité d'organisation du congrès"}</h2>
                  <div className="committee-grid organizing-grid">
                    {(aproposData.committeeMembers || [])
                      .filter(m => m.is_organizing)
                      .sort((a, b) => (a.order_organizing || 0) - (b.order_organizing || 0))
                      .map((member, idx) => (
                        <div key={member.id} className="committee-member" data-member-id={member.id}>
                          <div className="member-photo">
                            {member.logo && (
                              <div className="company-logo" >
                                <img
                                  src={member.logo}
                                  alt="Logo"
                                  style={{ width: '100%', height: '100%', objectFit: 'contain', maxWidth: '100%', maxHeight: '100%' }}
                                  onError={e => e.target.style.display = "none"}
                                />
                              </div>
                            )}
                            <img src={member.image || profile} alt={member.name} />
                          </div>
                          <div className="member-info">
                            <h4 style={{ margin: 0, textAlign: 'center' }}>{member.name}</h4>
                            <p>{renderExpandableText(member.title, member.id + '-title')}</p>
                            <p>{renderExpandableText(member.organization, member.id + '-org')}</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}


   {/* 2. COMITE SCIENTIFIQUE DU CONGRES */}
              <ScientificCommittee 
                members={(aproposData.committeeMembers || [])
                  .filter(m => m.is_scientific)
                  .sort((a, b) => (a.order_scientific || 0) - (b.order_scientific || 0))} 
                renderExpandableText={renderExpandableText}
              />



            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default APropos;


